import { setMilliseconds, sub } from 'date-fns';
import { createWithEqualityFn as create } from 'zustand/traditional';

import { Dimensions } from 'components/insights/constants';
import {
  DIMENSION_FILTER_KEY,
  getDefaultSlideoutDimension,
  VIEW_BY,
  VIEW_BY_DIMENSION_MAP,
  ViewByValue,
} from 'components/insights/referrals/constants';
import {
  filtersForSelectedGroupType,
  receivingClientFilterKey,
  receivingGroupFilterKey,
} from 'components/insights/referrals/helpers/filters';
import { DATES, END_DATE, NONE_OPTION, START_DATE } from 'constants/filterKeysConstants';
import GroupType from 'models/GroupType';

import { FilterOption, FilterState } from './types';

export type MetricDrillKey = 'response_time' | 'denial_rate' | 'acceptance_rate';

export type ReferralInsightsSlideoutContext =
  | { kind: 'entity'; entityId: string; entityName: string; dimension: Dimensions }
  | { kind: 'metric'; metric: MetricDrillKey };

export type OpenEntitySlideoutArgs = {
  entityId: string;
  entityName: string;
  dimension: Dimensions;
};

const now = setMilliseconds(new Date(), 0);
const thirtyDaysAgo = sub(now, { days: 30 });

export type ReferralsInsightsFilterState = FilterState<FilterOption[] | string | Date>;

type Actions = {
  setSelectedReceivingGroupType: (receivingGroupType: GroupType | undefined) => void;
  setSendingGroupTypeId: (id: string | undefined) => void;
  setViewBy: (viewBy: ViewByValue) => void;
  setSelectedFilters: (filters: ReferralsInsightsFilterState) => void;
  resetFilters: () => void;
  setExpanded: (expanded: boolean) => void;
  openSlideout: (context: OpenEntitySlideoutArgs) => void;
  openMetricSlideout: (metric: MetricDrillKey) => void;
  closeSlideout: () => void;
  setSlideoutDimension: (dimension: Dimensions) => void;
};

type State = {
  selectedReceivingGroupType: GroupType | undefined;
  sendingGroupTypeId: string | undefined;
  viewBy: ViewByValue;
  filters: ReferralsInsightsFilterState;
  expanded: boolean;
  slideoutContext: ReferralInsightsSlideoutContext | null;
  slideoutDimension: Dimensions | null;
  actions: Actions;
};

export const useReferralsInsightsStore = create<State>((set) => ({
  selectedReceivingGroupType: undefined,
  sendingGroupTypeId: undefined,
  viewBy: VIEW_BY.SENDING_GROUP,
  expanded: false,
  filters: {
    [START_DATE]: thirtyDaysAgo,
    [END_DATE]: now,
  },
  slideoutContext: null,
  slideoutDimension: null,
  actions: {
    setSelectedReceivingGroupType: (receivingGroupType) => set({ selectedReceivingGroupType: receivingGroupType }),
    setSendingGroupTypeId: (id) => set({ sendingGroupTypeId: id }),
    setViewBy: (viewBy) => set({ viewBy }),
    setSelectedFilters: (filters) => set((state) => ({ filters: { ...state.filters, ...filters } })),
    resetFilters: () =>
      set({
        viewBy: VIEW_BY.SENDING_GROUP,
        filters: {
          [START_DATE]: thirtyDaysAgo,
          [END_DATE]: now,
        },
      }),
    setExpanded: (expanded) => set({ expanded }),
    openSlideout: (args) =>
      set({
        slideoutContext: { kind: 'entity', ...args },
        slideoutDimension: getDefaultSlideoutDimension(args.dimension),
      }),
    openMetricSlideout: (metric) =>
      set((state) => ({
        slideoutContext: { kind: 'metric', metric },
        slideoutDimension: VIEW_BY_DIMENSION_MAP[state.viewBy],
      })),
    closeSlideout: () => set({ slideoutContext: null, slideoutDimension: null }),
    setSlideoutDimension: (dimension) => set({ slideoutDimension: dimension }),
  },
}));

export const useReferralsInsightsActions = () => useReferralsInsightsStore((state) => state.actions);

export const getReferralAnalyticsFilters = ({ filters, selectedReceivingGroupType, sendingGroupTypeId }: State) => {
  if (!selectedReceivingGroupType || !sendingGroupTypeId) return null;

  const activeFilters = filtersForSelectedGroupType(filters, selectedReceivingGroupType);
  const receivingClientKey = receivingClientFilterKey(selectedReceivingGroupType);
  const receivingGroupKey = receivingGroupFilterKey(selectedReceivingGroupType);

  const {
    [START_DATE]: start,
    [END_DATE]: end,
    [receivingClientKey]: receivingClient,
    [receivingGroupKey]: receivingGroup,
    ...rest
  } = activeFilters;

  return {
    ...rest,
    [DATES]: [{ start, end }],
    receivingClient,
    receivingGroup,
    receivingGroupType: selectedReceivingGroupType.id,
    sendingGroupType: sendingGroupTypeId,
  };
};

export const getSlideoutEntityFilter = (
  context: ReferralInsightsSlideoutContext
): Record<string, { id: string; name: string }[]> | null => {
  if (context.kind !== 'entity') return null;
  const key = DIMENSION_FILTER_KEY[context.dimension];
  if (!key) return null;
  // 'null' id is the API sentinel for IS NULL — the insights filter param is permitted as
  // an array, so a scalar would get stripped by strong params.
  if (!context.entityId || context.entityId === 'None') {
    return { [key]: [NONE_OPTION] };
  }
  return { [key]: [{ id: context.entityId, name: context.entityName }] };
};
